import { nextTick } from 'vue'
import { gsap, SplitText } from 'gsap/all'

import.meta.client && gsap.registerPlugin(SplitText)

export function createHeaderMotion({ el, loader, emitter, routeKey = '' }) {
	let headerContext = null
	let entryTimeline = null
	let splitTitle = null
	let hasAnimated = false

	const getAnimationTargets = () => {
		if (!el.value) {
			return {
				backgroundElements: [],
				upperTitleElement: null,
				titleElement: null,
				ctaElements: [],
				descriptionElements: [],
			}
		}

		const backgroundElements = Array.from(el.value.querySelectorAll('.inner-bg, .background-container'))
		const upperTitleElement = el.value.querySelector('.uppertitle')
		const titleElement = el.value.querySelector('.title')
		const ctaElements = Array.from(el.value.querySelectorAll('.ctas-container > *'))
		const descriptionElements = Array.from(el.value.querySelectorAll('.description-col .-body-big-regular'))

		return {
			backgroundElements,
			upperTitleElement,
			titleElement,
			ctaElements,
			descriptionElements,
		}
	}

	const setup = async () => {
		if (!import.meta.client || !el.value) return

		await nextTick()

		const { backgroundElements, upperTitleElement, titleElement, ctaElements, descriptionElements } = getAnimationTargets()

		backgroundElements.length && gsap.set(backgroundElements, { opacity: 0, scale: 0.8, transformOrigin: '50% 50%', autoAlpha: 1 })
		upperTitleElement && gsap.set(upperTitleElement, { y: 24, autoAlpha: 0 })
		titleElement && gsap.set(titleElement, { autoAlpha: 0 })
		ctaElements.length && gsap.set(ctaElements, { y: 24, autoAlpha: 0 })
		descriptionElements.length && gsap.set(descriptionElements, { y: 24, autoAlpha: 0 })
	}

	const init = async () => {
		if (!import.meta.client || !el.value || hasAnimated) return
		hasAnimated = true
		emitter?.emit?.('page-header-animation-start', { routeKey })

		await nextTick()
		const { backgroundElements, upperTitleElement, titleElement, ctaElements, descriptionElements } = getAnimationTargets()

		headerContext = gsap.context(() => {
			titleElement && gsap.set(titleElement, { autoAlpha: 1 })

			splitTitle?.revert()
			splitTitle = titleElement ? new SplitText(titleElement, { type: 'lines', linesClass: 'split-line' }) : null
			const titleLines = splitTitle?.lines || []

			titleLines.length && gsap.set(titleLines, { yPercent: 50, autoAlpha: 0 })

			entryTimeline = gsap.timeline()
			backgroundElements.length &&
				entryTimeline.to(
					backgroundElements,
					{
						opacity: 1,
						scale: 1,
						duration: 1.1,
						ease: 'power3.out',
					},
					0
				)

			upperTitleElement &&
				entryTimeline.to(
					upperTitleElement,
					{
						y: 0,
						autoAlpha: 1,
						duration: 0.6,
						ease: 'power3.out',
					},
					0.18
				)

			titleLines.length &&
				entryTimeline.to(
					titleLines,
					{
						yPercent: 0,
						autoAlpha: 1,
						duration: 0.75,
						stagger: 0.08,
						ease: 'power3.out',
					},
					0.24
				)

			ctaElements.length &&
				entryTimeline.to(
					ctaElements,
					{
						y: 0,
						autoAlpha: 1,
						duration: 0.6,
						stagger: 0.08,
						ease: 'power3.out',
					},
					0.36
				)

			descriptionElements.length &&
				entryTimeline.to(
					descriptionElements,
					{
						y: 0,
						autoAlpha: 1,
						duration: 0.6,
						ease: 'power3.out',
					},
					0.46
				)
		}, el.value)
	}

	const kill = () => {
		entryTimeline?.kill()
		entryTimeline = null

		splitTitle?.revert()
		splitTitle = null

		headerContext?.revert()
		headerContext = null

		if (!import.meta.client || !el.value) return

		const { backgroundElements, upperTitleElement, titleElement, ctaElements, descriptionElements } = getAnimationTargets()
		backgroundElements.length && gsap.set(backgroundElements, { clearProps: 'transform,transformOrigin,opacity,visibility' })
		upperTitleElement && gsap.set(upperTitleElement, { clearProps: 'transform,opacity,visibility' })
		titleElement && gsap.set(titleElement, { clearProps: 'transform,opacity,visibility' })
		ctaElements.length && gsap.set(ctaElements, { clearProps: 'transform,opacity,visibility' })
		descriptionElements.length && gsap.set(descriptionElements, { clearProps: 'transform,opacity,visibility' })
	}

	const mount = () => {
		if (!loader) {
			setup()
			init()
			return
		}

		loader.loaded.then(() => setup())
		loader.ready.then(() => init())
	}

	return {
		mount,
		kill,
	}
}
